/**
 * The inspector panel's shared styles — the Kai v2 light panel language
 * (white surface, bone hairlines, ink structure, mono-uppercase labels).
 *
 * Every style is a labeled `css` constant applied via the `css` prop, so the
 * generated class names are inspectable in the browser and map straight back
 * here. Only multi-consumer styles live in this file; a style used by exactly
 * one component is defined in that component's own file.
 */
import { css } from '@emotion/react';
import { bone, fieldWidth, focusRing, ink, midtone, monoLabel, paper, white } from '../../styles';

export const SECTION_STYLE = css`
  label: SECTION_STYLE;
  border-top: 1px solid ${bone};
  padding-top: 14px;
  margin-top: 6px;
  h2 {
    ${monoLabel}
    font-size: 10px; letter-spacing: 0.14em; color: ${midtone}; margin: 0 0 10px;
  }
`;

export const FIELD_ROW_STYLE = css`
  label: FIELD_ROW_STYLE;
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px; gap: 8px;
  label { color: ${midtone}; font-size: 13px; }
  /* Direct children only — the feet-and-inches widget styles its own inputs. */
  > input[type="number"] {
    box-sizing: border-box; width: ${fieldWidth}; padding: 6px 8px; border: 1px solid ${bone};
    font: inherit; text-align: right; background: ${white}; color: ${ink};
  }
  > input:focus, select:focus {
    outline: none; border-color: ${ink};
    box-shadow: ${focusRing};
  }
  select {
    padding: 6px 8px; border: 1px solid ${bone};
    font: inherit; background: ${white}; color: ${ink}; max-width: 158px;
  }
`;

export const BTN_STYLE = css`
  label: BTN_STYLE;
  ${monoLabel}
  display: inline-block; padding: 9px 14px; border: 1px solid ${bone};
  background: ${white}; cursor: pointer; font-size: 10.5px; color: ${ink};
  transition: border-color .12s, background .12s;
  &:hover { border-color: ${ink}; background: ${paper}; }
`;

/** An icon button (the header's undo and redo, the room turns): bone hairline on white, ink on hover. Each use sets its size. */
export const ICON_BUTTON_STYLE = css`
  label: ICON_BUTTON_STYLE;
  padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid ${bone}; background: ${white}; color: ${ink};
  cursor: pointer;
  transition: border-color .12s, background .12s;
  &:hover:not(:disabled) { border-color: ${ink}; background: ${paper}; }
  &:disabled { color: ${midtone}; opacity: 0.45; cursor: default; }
`;

export const BTN_ROW_STYLE = css`
  label: BTN_ROW_STYLE;
  display: flex; gap: 8px; flex-wrap: wrap;
`;

export const HINT_STYLE = css`
  label: HINT_STYLE;
  color: ${midtone}; font-size: 12px; line-height: 1.5;
`;
